/**
 * /ai-for-ontario-teachers — Ontario-lens response to Claude for
 * Teachers + ChatGPT for Teachers (both US-only, launched Jul 2026).
 *
 * The body copy on this page is FOUNDER-LOCKED, verbatim. Every
 * heading, sentence, and punctuation mark comes from the "LOCKED
 * COPY" spec block (Jul 2026 bounce). Do not tighten, reorder, add,
 * or remove a single word. Copy edits go through the founder;
 * `test_ai_for_ontario_teachers_page.py::test_page_locked_copy_verbatim`
 * blocks a silent rewrite.
 *
 * Structure (locked):
 *   Hero H1 + "Updated July 2026" italic
 *   Opening paragraph (Claude for Teachers + ChatGPT for Teachers)
 *   H2 "First, the good news" + two paragraphs
 *   H2 "Wall one: you can't get in" + paragraph
 *   H2 "Wall two: they don't speak Ontario" + paragraph
 *   RECEIPTS slot (empty ships null)
 *   H2 "Wall three: it forgets your year" + paragraph
 *   H2 "What I built instead" + two paragraphs (with **bolded** phrase)
 *   H2 "Try it without signing up for anything" + three free-door cards
 *   VIDEO slot (empty ships null)
 *   Dan blockquote
 *
 * NO pricing / paid-plan / trial / signup language anywhere. The
 * only calls to action are the three free-door cards.
 *
 * Palette: `#1c2d3d` navy ink, `#96ded1` mint accent, `#4a6984`
 * muted. Palette-guard tripwire blocks `#3EB489` and legacy tokens.
 */
import { Link } from "react-router-dom";
import UserMenu from "../components/UserMenu";
import useDocumentMeta from "../lib/useDocumentMeta";
import {
  VideoSlot,
  ReceiptsSlot,
} from "../components/AiForOntarioTeachersMediaSlots";
import media from "../shared/ai_for_ontario_teachers_media.json";

export default function AiForOntarioTeachersPage() {
  useDocumentMeta({
    title:
      "Claude for Teachers & ChatGPT for Teachers in Canada — What Ontario Teachers Should Know",
    description:
      "Anthropic and OpenAI both launched free AI for K-12 teachers — in the US. Here's what that means for Ontario classrooms, and what actually works with our curriculum.",
  });

  return (
    <div
      className="relative min-h-screen text-[#4a6984] overflow-x-clip"
      data-testid="ai-for-ontario-teachers-page"
    >
      {/* Ambient mint-blur atmosphere — matches the homepage
          (`components/AtomicEdu.jsx`) so this page sits inside the
          same visual world. Three fixed blur-3xl orbs, one warm
          upper-right, one softer mid-left, one lower-right. */}
      <div
        aria-hidden="true"
        className="fixed -top-32 -right-32 w-[44rem] h-[44rem] rounded-full bg-[#96ded1]/30 blur-3xl pointer-events-none z-0"
      />
      <div
        aria-hidden="true"
        className="fixed top-1/2 -left-40 w-[34rem] h-[34rem] rounded-full bg-[#96ded1]/15 blur-3xl pointer-events-none z-0"
      />
      <div
        aria-hidden="true"
        className="fixed -bottom-40 right-1/4 w-[30rem] h-[30rem] rounded-full bg-[#96ded1]/20 blur-3xl pointer-events-none z-0"
      />

      {/* Navy fixed header — identical treatment to the homepage
          `data-testid="site-nav"` block so the page reads as part of
          the marketing surface, not a bolt-on. Logo image + wordmark
          left, UserMenu right (renders the hamburger on mobile and
          the account chip when signed in). */}
      <header
        data-testid="aiot-site-nav"
        className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#1c2d3d]/95 border-b border-[#1c2d3d] shadow-lg shadow-[#1c2d3d]/10"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 shrink-0">
            <Link
              to="/"
              data-testid="aiot-brand-logo"
              className="font-black text-sm sm:text-base tracking-tight text-white/80 hover:text-white inline-flex items-center gap-2 whitespace-nowrap shrink-0"
            >
              <img
                src="/brand/atomicedu-logo-mark.png"
                alt="Atomic Edu"
                width="36"
                height="36"
                className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0"
                data-testid="aiot-brand-logo-mark"
              />
              Atomic Edu
            </Link>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 pt-24 pb-24">
        {/* HERO — H1 + updated-date + opening paragraph */}
        <section data-testid="aiot-hero" className="mb-12">
          <h1
            data-testid="aiot-title"
            className="font-black text-[#1c2d3d] tracking-tight text-4xl sm:text-5xl leading-[1.08]"
          >
            The big AI companies just built tools for teachers. Here&rsquo;s
            the Ontario picture.
          </h1>
          <p
            data-testid="aiot-updated"
            className="mt-5 text-sm italic text-[#4a6984] font-medium"
          >
            Updated July 2026
          </p>
          <p
            data-testid="aiot-lede"
            className="mt-6 text-[17px] text-[#1c2d3d] leading-[1.7]"
          >
            This month, Anthropic launched{" "}
            <strong>Claude for Teachers</strong> &mdash; free premium AI for
            K-12 educators, with lesson planning grounded in academic
            standards. OpenAI did the same last fall with{" "}
            <strong>ChatGPT for Teachers</strong>. If you&rsquo;ve seen the
            headlines and wondered whether this changes your Sunday nights,
            here&rsquo;s the honest version, from a teacher who has spent
            real evenings with these tools.
          </p>
        </section>

        {/* H2 — First, the good news */}
        <section data-testid="aiot-good-news" className="mb-12">
          <h2 className="text-2xl font-black text-[#1c2d3d] tracking-tight mb-5">
            First, the good news
          </h2>
          <div className="space-y-5 text-[#1c2d3d] leading-[1.7] text-[17px]">
            <p>
              These really are genuinely useful tools, if you&rsquo;re a
              teacher in the United States. Both programs verify American
              educators only, and both are built for American classrooms
              &mdash; state standards, American curriculum, privacy rules
              written to American law.
            </p>
            <p>
              None of that is wrong. It&rsquo;s just not built for us. But
              it means an Ontario teacher hits three walls.
            </p>
          </div>
        </section>

        {/* H2 — Wall one */}
        <section data-testid="aiot-wall-one" className="mb-12">
          <h2 className="text-2xl font-black text-[#1c2d3d] tracking-tight mb-5">
            Wall one: you can&rsquo;t get in
          </h2>
          <div className="space-y-5 text-[#1c2d3d] leading-[1.7] text-[17px]">
            <p>
              Verification requires a US school. That&rsquo;s the whole
              story. There is no Canadian version, and no date for one.
            </p>
          </div>
        </section>

        {/* H2 — Wall two, followed by RECEIPTS SLOT */}
        <section data-testid="aiot-wall-two" className="mb-12">
          <h2 className="text-2xl font-black text-[#1c2d3d] tracking-tight mb-5">
            Wall two: they don&rsquo;t speak Ontario
          </h2>
          <div className="space-y-5 text-[#1c2d3d] leading-[1.7] text-[17px]">
            <p>
              Even using the regular free versions of ChatGPT or Claude
              &mdash; which any of us can &mdash; the AI is guessing at our
              curriculum. Ask for a Grade 6 math lesson &ldquo;aligned to
              the Ontario curriculum&rdquo; and you&rsquo;ll get something
              that looks right and often isn&rsquo;t: expectation codes
              that don&rsquo;t exist, American strand names, no achievement
              chart categories, no Growing Success language, and a generic
              activity list instead of a three-part lesson. You end up
              checking every line against the curriculum documents &mdash;
              which was the job you were trying to save time on.
            </p>
          </div>
          <ReceiptsSlot items={media.receipts} />
        </section>

        {/* H2 — Wall three */}
        <section data-testid="aiot-wall-three" className="mb-12">
          <h2 className="text-2xl font-black text-[#1c2d3d] tracking-tight mb-5">
            Wall three: it forgets your year
          </h2>
          <div className="space-y-5 text-[#1c2d3d] leading-[1.7] text-[17px]">
            <p>
              This is the one that matters most, and no chatbot fixes it.
              ChatGPT doesn&rsquo;t know your long-range plan. It
              doesn&rsquo;t know what you taught in October when
              you&rsquo;re writing report cards in January. Every night
              you start from zero: paste the expectation, explain the
              format, describe your class. The prompting <em>is</em> the
              work.
            </p>
          </div>
        </section>

        {/* H2 — What I built instead */}
        <section data-testid="aiot-built-instead" className="mb-12">
          <h2 className="text-2xl font-black text-[#1c2d3d] tracking-tight mb-5">
            What I built instead
          </h2>
          <div className="space-y-5 text-[#1c2d3d] leading-[1.7] text-[17px]">
            <p>
              I&rsquo;ve taught Grades 4&ndash;8 in Ontario for 20 years.
              Atomic Edu is the version of this that starts from our side
              of the border: the actual Ontario expectations built in
              &mdash; verbatim, not approximated &mdash; lessons in
              three-part structure, assessment in Growing Success language,
              and a planning spine that remembers your year so report card
              comments come from what you actually taught.
            </p>
            <p>
              The difference that matters: Atomic Edu is built for student
              privacy. It never asks for a student&rsquo;s name or account
              &mdash; it&rsquo;s designed never to need one. The American
              tools may not require student data either, but nothing in
              them works to keep it out. Here, privacy is the default, not
              your job to remember.
            </p>
          </div>
        </section>

        {/* H2 — Try it (three free-door cards) */}
        <section data-testid="aiot-try-it" className="mb-4">
          <h2 className="text-2xl font-black text-[#1c2d3d] tracking-tight mb-6">
            Try it without signing up for anything
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <ToolCard
              testId="aiot-tool-free-lesson"
              to="/#try-it"
              title="Generate a free lesson"
              body="no account, no card — just an email address"
            />
            <ToolCard
              testId="aiot-tool-supply-plan"
              to="/supply-plans"
              title="Emergency supply plan"
              body="sick at 6 a.m., plan by 6:15, free forever"
            />
            <ToolCard
              testId="aiot-tool-classroom-support"
              to="/classroom-support"
              title="Classroom Support Hub"
              body="ADHD, gifted, autism, and 2e guides, free, no login"
            />
          </div>

          {/* VIDEO SLOT — sits AFTER the "Try it" cards per spec. */}
          <VideoSlot config={media.video} />
        </section>

        {/* Dan blockquote */}
        <section data-testid="aiot-dan-quote" className="mt-12">
          <blockquote className="border-l-4 border-[#96ded1] pl-6 py-2 text-[#1c2d3d] text-[17px] leading-[1.7] italic">
            <p>
              I built Atomic Edu because I was the teacher writing report
              card comments at midnight, re-typing the same accommodations,
              and losing my Sundays to planning. Every feature in here
              exists because I needed it on a school night.
            </p>
            <footer className="mt-3 not-italic text-sm font-bold text-[#4a6984]">
              &mdash; Dan
            </footer>
          </blockquote>
        </section>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------
// Tool card — one of the three free-door blocks. Keeps the card
// treatment from the prior build (mint hover border + arrow-hint on
// title). Body is a single terse phrase per the LOCKED COPY.
// ---------------------------------------------------------------------
function ToolCard({ testId, to, title, body }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group block rounded-lg border border-[#4a6984]/15 bg-white p-5 hover:border-[#96ded1] hover:shadow-md transition-all"
    >
      <h3 className="font-black text-[17px] text-[#1c2d3d] tracking-tight leading-tight">
        {title}
        <span className="ml-1 inline-block transition-transform group-hover:translate-x-0.5">
          →
        </span>
      </h3>
      <p className="mt-2 text-[14px] text-[#4a6984] leading-relaxed">
        {body}
      </p>
    </Link>
  );
}
